// Routes the shell's overlays use: the Help drawer and the emergency line.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

// ── Help (src/aithora/v3/atlas/help.ts) ─────────────────────────────────────

export interface HelpSource {
  slug: string;
  title: string;
  /** The page the entry is about, in the design's view names ({ view: 'worklist' }). */
  link: { view?: string; tab?: string; [k: string]: unknown } | null;
}

export interface HelpAskResponse {
  real: true;
  /** The logged question's id, for the helped / did not help feedback. */
  id: string;
  /** The written answer, or null when no model is reachable or nothing matched. */
  answer: string | null;
  /** True only when `answer` was written by the model from the matching help docs. */
  answerLive: boolean;
  sources: HelpSource[];
  retrieval: 'vector' | 'keyword';
  /** Why there is no written answer, when there is none. */
  reason?: string;
}

/** POST /help/ask { question, page }: answered only from the help docs, every question logged. */
export const helpAsk = route<HelpAskResponse, void, { question: string; page?: string }>()('POST', `${A}/help/ask`, { honest: true, readOnlySafe: true });

/** POST /help/:id/feedback { helped }: only the person who asked. */
export const helpFeedback = route<{ real: true; id: string; helped: boolean }, void, { helped: boolean }>()('POST', `${A}/help/:id/feedback`, { honest: true, readOnlySafe: true });

// ── Emergencies (src/aithora/router.ts, Emergency response) ─────────────────

/** One open incident as GET /emergency/incidents returns it (the fields the shell reads). */
export interface EmergencyIncident {
  id: string;
  type: string | null;
  status: 'active' | 'requires_emergency_services' | string;
  created_at: string;
  /** Set once someone said "I'm going"; null means nobody has claimed it. */
  acknowledged_at: string | null;
  acknowledgedByName: string | null;
  /** Name and room only for this building's care staff and Aithora staff; null otherwise. */
  residentName: string | null;
  room: string | null;
  /** CRITICAL, HIGH or MEDIUM (emergency_incidents.severity). */
  severity?: string | null;
  /** Set when 911 is part of the plan (status requires_emergency_services as well). */
  requires_911?: boolean | null;
}

/** GET /emergency/incidents: the building's open incidents, newest first. Staff only. */
export const emergencyIncidents = route<{ incidents: EmergencyIncident[] }>()('GET', `${A}/emergency/incidents`);

/** POST /emergency/acknowledge { incidentId }: "I'm going". first = the person who claimed it; at911 = the 911 step, whose alerts keep going until it is closed (AF-1616). 409 when it is already closed. */
export const emergencyAcknowledge = route<{ ok: boolean; first?: boolean; at911?: boolean; again?: boolean }, void, { incidentId: string }>()('POST', `${A}/emergency/acknowledge`);
/** POST /emergency/acknowledge/undo: the 10-second Undo after "I'm going" (AF-1611); 409 once it has run out. */
export const emergencyAcknowledgeUndo = route<{ ok: boolean }, void, { incidentId: string }>()('POST', `${A}/emergency/acknowledge/undo`);

/** The fields the severity order reads; every incident shape the apps hold has them. */
export interface EmergencyRankable {
  type?: string | null;
  status?: string | null;
  severity?: string | null;
  requires_911?: boolean | null;
  created_at: string;
}

/** Kinds that are a crisis of the person themselves: they go first with a 911 call. */
export const CRISIS_EMERGENCY_TYPES: ReadonlySet<string> = new Set(['SELF_HARM', 'COGNITIVE_CRISIS', 'CRISIS', 'UNSAFE_FEELING']);

/** Still open: nobody has resolved it. */
export function isOpenEmergency(i: Pick<EmergencyRankable, 'status'>): boolean {
  return i.status === 'active' || i.status === 'requires_emergency_services';
}

/**
 * The one severity order every surface uses (console banner, Guardian, the phone):
 *   0  911 is called for (requires_emergency_services / requires_911), or self-harm or crisis;
 *   1  any other CRITICAL;
 *   2  the rest.
 */
export function emergencyTier(i: EmergencyRankable): 0 | 1 | 2 {
  if (i.status === 'requires_emergency_services' || i.requires_911 === true) return 0;
  if (CRISIS_EMERGENCY_TYPES.has(String(i.type ?? '').toUpperCase())) return 0;
  if (String(i.severity ?? '').toUpperCase() === 'CRITICAL') return 1;
  return 2;
}

/** Worst first: by tier, then the one waiting longest (oldest first) within a tier. */
export function compareEmergencies(a: EmergencyRankable, b: EmergencyRankable): number {
  const t = emergencyTier(a) - emergencyTier(b);
  if (t) return t;
  const ta = Date.parse(a.created_at);
  const tb = Date.parse(b.created_at);
  return (Number.isFinite(ta) ? ta : Infinity) - (Number.isFinite(tb) ? tb : Infinity);
}

/** A sorted copy, worst first. */
export function sortEmergencies<T extends EmergencyRankable>(list: readonly T[]): T[] {
  return list.slice().sort(compareEmergencies);
}

/**
 * The emergency the red line names: the MOST SEVERE open incident nobody has claimed yet
 * (the design's em.stage === 'new'), oldest first within the same severity, or null.
 */
export function unclaimedEmergency<T extends EmergencyRankable & { acknowledged_at: string | null }>(incidents: readonly T[]): T | null {
  return sortEmergencies(incidents.filter((i) => !i.acknowledged_at && isOpenEmergency(i)))[0] ?? null;
}

// ── View as (src/aithora/v3/view-as.ts; enforced for every route by src/core/view-as.ts) ──

/** One running View as session. */
export interface ViewAsSession {
  id: string;
  /** The stored role being viewed as. */
  role: string;
  /** The facility picked for a facility role, or the Super Admin's own organization. */
  organizationId: string | null;
  facilityName: string | null;
  /**
   * The viewed building is one seeded demo building: the console shows one "Demo
   * building" banner instead of a SAMPLE tag on every figure. Absent on an older server.
   */
  demoBuilding?: boolean;
  startedAt: string;
  /** A session lasts 4 hours, then has to be started again. */
  expiresAt: string;
}

/**
 * POST /view-as/start { role, organizationId? }: Super Admin only, audited. Starting again
 * switches (the old one is ended and logged). Facility roles take the facility picked
 * from GET /facilities/search; with none, `needsFacility` is true and building blocks
 * are empty. The server then honours X-Aithora-View-As on every route and refuses writes.
 */
export const viewAsStart = route<{ real: true; session: ViewAsSession | null; needsFacility: boolean }, void, { role: string; organizationId?: string | null }>()(
  'POST', `${A}/view-as/start`, { honest: true, readOnlySafe: true },
);

/** POST /view-as/end: back to your own role, audited. */
export const viewAsEnd = route<{ real: true; ended: number }>()('POST', `${A}/view-as/end`, { honest: true, readOnlySafe: true });

/** GET /view-as/current: the running session of this sign-in, or null. */
export const viewAsCurrent = route<{ real: true; session: ViewAsSession | null }>()('GET', `${A}/view-as/current`, { honest: true });

export const shellApi = { helpAsk, helpFeedback, emergencyIncidents, emergencyAcknowledge, emergencyAcknowledgeUndo, viewAsStart, viewAsEnd, viewAsCurrent } as const;
